'use client';

import bbox from '@turf/bbox';
import { featureCollection, lineString } from '@turf/helpers';
import { useMemo } from 'react';
import { SPACE_600 } from '@alltrails/denali/tokens';
import type { AllTrailsBounds, LngLat } from '@alltrails/maps/types/Geo';
import { useAppSelector } from '../redux';

// Matches the breathing room useFitMapToRouteBounds gives routes on the explore map
const BOUNDS_PADDING_PX = SPACE_600;

type PrintRoute = {
  bounds: AllTrailsBounds | null;
};

/**
 * How the print frames the custom route the page is showing: the same bounds the explore map fits
 * to, the server-provided route bounds when present and computed from the paths otherwise. Null
 * when the page has no route, or none with a path long enough to draw, so there is nothing to frame.
 */
const usePrintRoute = (): PrintRoute => {
  const { routeBounds, segments } = useAppSelector(state => ({
    routeBounds: state.customRoute?.route.bounds,
    segments: state.customRoute?.route.segments
  }));

  return useMemo(() => {
    const drawnPaths = (segments ?? []).flatMap(segment => segment.paths.filter(path => path.points.length > 1));

    if (drawnPaths.length === 0) {
      return { bounds: null };
    }

    if (routeBounds) {
      return { bounds: { ...routeBounds, padding: BOUNDS_PADDING_PX } };
    }

    const [minLng, minLat, maxLng, maxLat] = bbox(
      featureCollection(drawnPaths.map(path => lineString(path.points.map(([lat, lng]) => [lng, lat] as LngLat))))
    );

    return {
      bounds: {
        latitudeTopLeft: maxLat,
        longitudeTopLeft: minLng,
        latitudeBottomRight: minLat,
        longitudeBottomRight: maxLng,
        padding: BOUNDS_PADDING_PX
      }
    };
  }, [routeBounds, segments]);
};

export default usePrintRoute;
